(function () {
  class CartApiError extends Error {
    constructor(message, response, data) {
      super(message);
      this.name = 'CartApiError';
      this.response = response;
      this.data = data;
    }
  }

  class CartApi {
    static mutationQueue = Promise.resolve();

    static root() {
      const root = window.Shopify?.routes?.root || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    static endpoint(path) {
      return `${this.root()}${String(path).replace(/^\//, '')}`;
    }

    static async request(path, options = {}) {
      let response;

      try {
        response = await fetch(this.endpoint(path), {
          ...options,
          headers: {
            Accept: 'application/json',
            ...options.headers
          }
        });
      } catch (error) {
        throw new CartApiError(
          'We could not reach the basket. Check your connection and try again.',
          null,
          { cause: error }
        );
      }

      const data = await response.json().catch(function () {
        return {};
      });

      if (!response.ok) {
        throw new CartApiError(
          data.description || data.message || 'The basket could not be updated.',
          response,
          data
        );
      }

      return data;
    }

    static enqueue(operation) {
      const queued = this.mutationQueue.then(operation, operation);

      this.mutationQueue = queued.catch(function () {
        // Keep the queue usable after an individual request fails.
      });

      return queued;
    }

    static get() {
      return this.request('cart.js', {
        cache: 'no-store'
      });
    }

    static add(payload) {
      return this.enqueue(() =>
        this.request('cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
      );
    }

    static addForm(formData) {
      return this.enqueue(() =>
        this.request('cart/add.js', {
          method: 'POST',
          body: formData
        })
      );
    }

    static change(key, quantity) {
      return this.enqueue(async () => {
        await this.request('cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity: Math.max(0, Number(quantity || 0))
          })
        });

        // The mutation response can contain a transient discount allocation.
        // Fetch the canonical cart before rerendering totals and discounts.
        return this.request('cart.js', {
          cache: 'no-store'
        });
      });
    }

    static changeLine(line, quantity) {
      return this.enqueue(async () => {
        await this.request('cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            line: Math.max(1, Number(line || 1)),
            quantity: Math.max(0, Number(quantity || 0))
          })
        });

        // Discount codes are recalculated after the line mutation. Always
        // rerender from a fresh cart response rather than the mutation body.
        return this.request('cart.js', {
          cache: 'no-store'
        });
      });
    }

    static update(payload) {
      return this.enqueue(() =>
        this.request('cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        })
      );
    }

    static dispatchUpdated(cart, source = 'unknown') {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            itemCount: cart.item_count,
            totalPrice: cart.total_price,
            source,
            cart
          }
        })
      );
    }
  }

  window.CartApi = CartApi;
  window.CartApiError = CartApiError;
})();
